import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { tokens } from '@ftedn/shared';
import { Button } from '../Button';
import { Card } from '../Card';
import styles from './Modal.module.css';

export interface ModalProps {
  actions?: ReactNode;
  children: ReactNode;
  description?: string;
  onClose: () => void;
  open: boolean;
  title: string;
  width?: string;
}

export function Modal({ actions, children, description, onClose, open, title, width = tokens.breakpoint.md }: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <Card
        className={styles.surface}
        onClick={(event) => event.stopPropagation()}
        padding="lg"
        style={{ '--ui-modal-max-width': width } as CSSProperties}
        tone="glass"
      >
        <Button aria-label="Close modal" className={styles.close} onClick={onClose} size="sm" variant="ghost">
          Close
        </Button>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <div>{children}</div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </Card>
    </div>
  );
}
